/*
 * Description:
 * ---------------------
 * The master map, Remote Devices' main screen, as wide as the screen and
 * reaching down to the window's foot: every box of the customer, the ones
 * that share nothing too, each a card standing for its network, and one line
 * per direction between two boxes for what devices at one may connect to at
 * the other, with how many, from the box's own port, a circle beside its name
 * standing for all its devices, since access is granted to the whole network.
 * A box's chevron opens it to list its devices inside it, each with a port of
 * its own: the lines into it then end at the port of each device they reach.
 * A port is filled where a line meets it. Several boxes stay open, and opening one moves only the boxes under
 * it. Picking a box, a device or a line lights all it takes part in (the
 * network row a line leaves from, the line, the device it ends at) and opens
 * the panel of the box it is about (BoxPanel) at the map's right: every device
 * there and who may reach each from where, with a picked line's rule open in
 * it. Up to three panels stay open, each dragged by its head to wherever it
 * covers least, the last one touched on top; moving one moves nothing else. On
 * the map a line's address shows only once it is picked. Drag to pan; the
 * buttons, or Ctrl with the wheel, zoom; Fit map shows it all beside the docked
 * panels, and keeps doing so as boxes open and panels open or close, until the
 * map is moved by hand. Focus
 * opens one box's own view (AccessCanvas). Open boxes, panels and the pick are
 * the panel's, so Back to master map finds them as they were. Geometry is
 * mapLayout.js, wording reachWords.js.
 *
 * ! file components/MasterMap.js
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronRight, FiGrid, FiMaximize2, FiMinus, FiPlus } from "react-icons/fi";
import { TbRouter } from "react-icons/tb";
import useBoxReads from "../hooks/useBoxReads";
import usePhoneLayout from "../hooks/usePhoneLayout";
import BoxPanel from "./BoxPanel";
import * as L from "./mapLayout";
import { DeviceIcon, getDeviceType } from "./NetworkCanvas";
import * as W from "./reachWords";

const TIPS = ["good", "wait", "bad", "off", "on", "on-bad"];
// A line for several devices takes the worst state among them.
const WORST = ["bad", "wait", "good", "off"];
// An open box lists every device it shares, and FEW of the others until asked for all.
const FEW = 6;
// The view reaches down to the window's foot, less FOOT for the screen's own margin under it, and is never under VIEW_MIN.
const VIEW_MIN = 420;
const FOOT = 20;
const ZOOM_MIN = 0.25;
// The first view never starts smaller than this, so a narrow screen opens readable; Fit map still shows all.
const READABLE = 0.5;
// A small map fits larger than life, up to FIT_MAX.
const FIT_MAX = 1.25;
const ZOOM_MAX = 2;
const STEP = 1.25;

const worst = (tones) => WORST.find((t) => tones.includes(t));
const pressed = (go) => (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    go();
  }
};
const iconOf = (r) => (r.net ? <FiGrid size={15} /> : <DeviceIcon type={getDeviceType(r.device || { hostname: r.name })} size={15} />);
// A panel in the phone's list stands where it is listed: nothing to drag or close.
const STILL = { at: {}, z: 0, top: false, onGrab: {}, onClose: () => {} };
const rowKey = (entry) => (W.isRange(entry.lan_address) ? "net" : entry.lan_address);
const rulesOn = (line) => (line.rule ? [line.rule] : line.pair.rules);

export default function MasterMap({
  group, sites, entries, view, open, onOpen, panels, onPanels, selection, onSelection, canWrite, busy,
  onFocus, onAllow, onManage, onToggle, onRemoveAccess,
}) {
  const anchors = view.anchors;
  const phone = usePhoneLayout();
  const byId = useMemo(() => Object.fromEntries(sites.map((s) => [s.node_id, s])), [sites]);
  const ids = useMemo(() => sites.map((s) => s.node_id), [sites]);
  const rules = useMemo(() => W.rulesOf(entries).filter((r) => byId[r.box] && byId[r.entry.site] && r.box !== r.entry.site), [entries, byId]);
  const pairs = useMemo(() => {
    const out = new Map();
    for (const r of rules) {
      const key = `${r.box}>${r.entry.site}`;
      if (!out.has(key)) out.set(key, { key, from: r.box, to: r.entry.site, rules: [] });
      out.get(key).rules.push(r);
    }
    return [...out.values()];
  }, [rules]);
  const cols = useMemo(() => L.columns(ids, pairs.map((p) => ({ from: p.from, to: p.to, n: p.rules.length }))), [ids, pairs]);
  const opened = open.filter((id) => byId[id]);
  const shown = panels.filter((p) => byId[p.id]);
  // Every box open on the map or in a panel, or every box in the phone's list, is asked what devices it sees.
  const asked = [...new Set([...opened, ...shown.map((p) => p.id), ...(phone ? ids : [])])].filter((id) => W.readiness(byId[id]) !== "update");
  const { reads: devices } = useBoxReads(asked, "/diagnostics/devices", 30000);
  const [all, setAll] = useState({});
  const held = Object.fromEntries(asked.map((id) => [id, contents(entries, byId[id], devices[id])]));
  const boxes = Object.fromEntries(opened.map((id) => [id, onMap(held[id] || contents(entries, byId[id]), all[id])]));
  const reaching = (id) => rules.filter((r) => r.box === id);
  const { pos, w, h } = L.place(cols, Object.fromEntries(opened.map((id) => [id, L.boxHeight(boxes[id].rows.length, boxes[id].notes.length)])));

  const rule = selection?.kind === "rule" ? rules.find((r) => r.key === selection.key) || null : null;
  // The rule picked, and each open in a panel, are asked about at both its ends.
  const watched = [rule, ...shown.map((p) => rules.find((r) => r.key === p.mark.rule))].filter(Boolean);
  const { reads, ask } = useBoxReads([...new Set(watched.flatMap((r) => [r.box, r.entry.site]))], "/reach", 15000);
  const check = rule && W.setupOf(rule.entry, rule.box, anchors).state === "applied"
    ? W.addressCheckOf(rule.entry, rule.box, rule.address, byId[rule.box], reads[rule.box]) : null;
  const toneOf = (r) => W.pathTone(W.setupOf(r.entry, r.box, anchors), r === rule ? check : null);
  const lines = pairs.flatMap((p) => {
    if (!boxes[p.to]) {
      return [{ key: p.key, from: p.from, to: p.to, pair: p, row: null, tone: worst(p.rules.map(toneOf)), label: W.pairLabel(p.rules) }];
    }
    return p.rules.map((r) => {
      const i = boxes[p.to].rows.findIndex((x) => x.entry?.id === r.entry.id);
      return { key: r.key, from: p.from, to: p.to, rule: r, row: i < 0 ? null : i, tone: toneOf(r), label: r === rule ? r.address : "" };
    });
  });
  const { paths, ports } = L.connect(lines, pos, Object.fromEntries(opened.map((id) => [id, boxes[id].rows.length])));
  // The world takes in the lines that bulge out of a column, and the boxes shift right by what bulges left.
  const pad = L.margins(lines, pos);
  const world = { w: w + pad.left + pad.right, h };
  const lit = litBy(selection, lines, rules, entries, boxes);
  const litPorts = new Set(lit ? lines.filter((l) => lit.lines.has(l.key)).flatMap((l) => paths[l.key].ends) : []);

  const docked = shown.filter((p) => p.x == null).map((p) => p.slot);
  const { viewport, cam, size, zoom, fitAll, drag } = useCamera(world.w, world.h, docked, shown.map((p) => p.id).join(), () => onSelection(null));
  const spots = L.panelSpots(shown, size.w, size.h);
  const grab = useGrab(onPanels, spots);
  const topZ = Math.max(0, ...shown.map((p) => p.z));

  const fold = (id) => {
    if (!open.includes(id)) return onOpen([...open, id]);
    onOpen(open.filter((x) => x !== id));
    if (selection?.kind === "device" && selection.box === id) onSelection(null);
  };
  const show = (id, mark) => onPanels((ps) => L.openPanel(ps, id, mark));
  const pickBox = (id) => {
    onSelection({ kind: "box", id });
    show(id, {});
  };
  const pickDevice = (box, key) => {
    onSelection({ kind: "device", box, key });
    show(box, { device: key });
  };
  // A rule opens in its owner's panel, its owner open on the map so the line ends at the device; closed, it leaves the device picked.
  const pickRule = (r, on = true) => {
    if (!on) return pickDevice(r.entry.site, rowKey(r.entry));
    if (!open.includes(r.entry.site)) onOpen([...open, r.entry.site]);
    onSelection({ kind: "rule", key: r.key });
    return show(r.entry.site, { rule: r.key });
  };
  const pickPair = (p) => {
    onSelection({ kind: "pair", from: p.from, to: p.to });
    show(p.to, { from: p.from });
  };

  const panelFor = (s, p, place) => (
    <BoxPanel key={s.node_id} site={s} byId={byId} {...place} mark={p?.mark || {}}
      inside={held[s.node_id] || contents(entries, s)}
      rules={rules} anchors={anchors} reads={reads} canWrite={canWrite} busy={busy}
      onPickDevice={(key) => pickDevice(s.node_id, key)}
      onPickRule={pickRule} onRefresh={ask} onFocus={onFocus} onAllow={onAllow} onManage={onManage}
      onToggle={onToggle} onRemoveAccess={onRemoveAccess} />
  );
  const allowAll = canWrite && sites.length > 1 && (
    <button type="button" className="dl-btn primary" disabled={busy} onClick={() => onAllow({})}><FiPlus size={14} /> {W.ALLOW_ACCESS}</button>
  );

  // A phone has no room for the drawing: every box's panel, one under another,
  // with the same rows, addresses and actions, a pick marking its rows in place.
  if (phone) {
    return (
      <section className="panel rd-map rd-list" aria-label={W.MAP_LABEL}>
        <header className="rd-map-head">
          {sites.length === 1 && <span className="rd-quiet">{W.onlyBox(sites[0].node_id, group.name)}</span>}
          {allowAll}
        </header>
        {sites.map((s) => panelFor(s, shown.find((p) => p.id === s.node_id), STILL))}
      </section>
    );
  }

  return (
    <section className="panel rd-map" aria-label={W.MAP_LABEL}>
      <header className="rd-map-head">
        <div className="rd-map-zoom">
          <button type="button" className="dl-btn icon" aria-label={W.ZOOM_OUT} onClick={() => zoom(1 / STEP)}><FiMinus size={14} /></button>
          <button type="button" className="dl-btn icon" aria-label={W.ZOOM_IN} onClick={() => zoom(STEP)}><FiPlus size={14} /></button>
          <button type="button" className="dl-btn rd-small" onClick={fitAll}><FiMaximize2 size={13} /> {W.FIT}</button>
          {sites.length === 1 && <span className="rd-quiet">{W.onlyBox(sites[0].node_id, group.name)}</span>}
        </div>
        {allowAll}
      </header>
      <div className="rd-map-body">
        <div className="rd-map-view" ref={viewport} style={{ height: size.h }} {...drag}>
          <div className="rd-map-world" style={{ width: world.w, height: world.h,
            transform: `translate(${cam.x + pad.left * cam.k}px, ${cam.y}px) scale(${cam.k})` }}>
            <svg className="rd-map-lines" width={w} height={h}>
              <defs>
                {TIPS.map((t) => (
                  <marker key={t} id={`rd-tip-${t}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="10" markerHeight="10"
                    markerUnits="userSpaceOnUse" orient="auto">
                    <path d="M 1 1.5 L 9 5 L 1 8.5 z" className={`rd-tip rd-${t}`} />
                  </marker>
                ))}
              </defs>
              {lines.map((l) => {
                const p = paths[l.key];
                const on = lit?.lines.has(l.key);
                const picked = l.rule ? selection?.kind === "rule" && selection.key === l.key
                  : selection?.kind === "pair" && selection.from === l.from && selection.to === l.to;
                const go = () => (l.rule ? pickRule(l.rule) : pickPair(l.pair));
                return (
                  <g key={l.key} className={`rd-map-line rd-${l.tone}${on ? " is-on" : ""}${lit && !on ? " is-dim" : ""}`}
                    role="button" tabIndex={0} aria-pressed={!!picked} onClick={go} onKeyDown={pressed(go)}
                    aria-label={l.rule ? W.ruleLine(l.rule) : W.pairLine(l.from, l.to, l.pair.rules)}>
                    <path d={p.d} className="rd-map-hit" />
                    <path d={p.d} className="rd-map-path" markerEnd={`url(#rd-tip-${on ? (l.tone === "bad" ? "on-bad" : "on") : l.tone})`} />
                    {p.label && (
                      <g className={`rd-map-label${l.rule ? " is-address" : ""}`} transform={`translate(${p.label.x} ${p.label.y})`}>
                        <rect x={-p.label.w / 2} y={-11} width={p.label.w} height={22} rx={11} />
                        <text dy="0.35em">{p.label.text}</text>
                      </g>
                    )}
                  </g>
                );
              })}
              {ports.map((p) => (
                <g key={p.key} aria-hidden="true"
                  className={`rd-port${p.used ? " is-used" : ""}${litPorts.has(p.key) ? " is-on" : ""}${lit && !litPorts.has(p.key) ? " is-dim" : ""}`}>
                  <line x1={p.x0} y1={p.y} x2={p.x} y2={p.y} />
                  <circle cx={p.x} cy={p.y} r={p.row == null ? L.BOX_R : L.DEVICE_R} />
                </g>
              ))}
            </svg>
            {sites.map((s) => {
              const id = s.node_id;
              return (
                <Box key={id} site={s} at={pos[id]} inside={boxes[id]} picked={selection?.kind === "box" && selection.id === id}
                  lit={!!lit?.near.has(id)} dim={!!lit && !lit.near.has(id)} rowsLit={lit?.rows}
                  pickedRow={selection?.kind === "device" && selection.box === id ? selection.key : null}
                  summary={W.boxSummary(entries.filter((e) => e.site === id), reaching(id), anchors)}
                  onFold={() => fold(id)} onPick={() => pickBox(id)}
                  onPickRow={(key) => pickDevice(id, key)} onMore={() => setAll((a) => ({ ...a, [id]: !a[id] }))} />
              );
            })}
          </div>
        </div>
        {shown.map((p) => panelFor(byId[p.id], p, {
          at: spots[p.id], z: p.z, top: p.z === topZ, height: size.h - spots[p.id].y - 16, onGrab: grab(p.id),
          onClose: () => onPanels((ps) => ps.filter((x) => x.id !== p.id)),
        }))}
      </div>
    </section>
  );
}

/* What a box holds: its network shared whole, if it is, then every device it
   shares, then the others its box sees; and how its reading stands. read is
   the box's answer on /diagnostics/devices, absent until it comes. */
function contents(entries, site, read) {
  if (W.readiness(site) === "update") return { rows: [], notes: [W.UPDATE_NOTE] };
  const { rows, net } = W.hereRows(entries, site.node_id, W.lanOf(site), read?.body?.devices, read ? !!read.body : undefined);
  const notes = !read ? [W.READING] : !read.body ? [W.SILENT] : !rows.length && !net ? [W.NONE_SEEN] : [];
  const head = net ? [{ key: "net", name: net.name, ip: net.lan_address, entry: net, net: true }] : [];
  return { rows: [...head, ...rows], notes };
}

/* What an open box on the map lists: every device it shares and FEW of the
   others, all of them once asked for, with the notes under them. */
function onMap(inside, all) {
  const rest = inside.rows.filter((r) => !r.entry);
  const notes = inside.notes.map((text) => ({ text }));
  if (rest.length <= FEW) return { rows: inside.rows, notes };
  const rows = all ? inside.rows : inside.rows.filter((r) => r.entry || rest.indexOf(r) < FEW);
  return { rows, notes: [...notes, { more: true, text: all ? W.SHOW_FEWER : W.showMore(rest.length - FEW) }] };
}

/* What a pick lights: the lines it takes part in, the boxes at their ends,
   and, in an open box, the device row a line ends at. */
function litBy(selection, lines, rules, entries, boxes) {
  if (!selection) return null;
  const out = { lines: new Set(), near: new Set(), rows: new Set() };
  const light = (l) => {
    out.lines.add(l.key);
    out.near.add(l.from).add(l.to);
    if (l.rule && boxes[l.to]) out.rows.add(`${l.to}|${rowKey(l.rule.entry)}`);
  };
  if (selection.kind === "box") {
    out.near.add(selection.id);
    for (const l of lines) if (l.from === selection.id || l.to === selection.id) light(l);
  } else if (selection.kind === "pair") {
    for (const l of lines) if (l.from === selection.from && l.to === selection.to) light(l);
  } else if (selection.kind === "device") {
    const entry = entries.find((e) => e.site === selection.box && rowKey(e) === selection.key);
    out.near.add(selection.box);
    out.rows.add(`${selection.box}|${selection.key}`);
    for (const l of lines) if (entry && rulesOn(l).some((r) => r.entry.id === entry.id)) light(l);
  } else if (selection.kind === "rule") {
    const r = rules.find((x) => x.key === selection.key);
    for (const l of lines) if (rulesOn(l).some((x) => x.key === selection.key)) light(l);
    if (r) out.rows.add(`${r.entry.site}|${rowKey(r.entry)}`);
  }
  return out;
}

/* The height from an element's top down to the foot of the area it scrolls
   in, with that area scrolled to its start. */
function toFoot(el, scroller) {
  const top = el.getBoundingClientRect().top;
  if (!scroller) return window.innerHeight - top - window.scrollY;
  return scroller.clientHeight - (top - scroller.getBoundingClientRect().top + scroller.scrollTop);
}

const scrollerOf = (el) => {
  let s = el.parentElement;
  while (s && !/auto|scroll/.test(window.getComputedStyle(s).overflowY)) s = s.parentElement;
  return s;
};

/* useCamera pans and zooms the map, whose view reaches down to the window's
   foot: drag on the map to pan, zoom about the middle or, with Ctrl and the
   wheel, about the pointer. It starts in fit mode, all of it in view beside
   the panels docked (their slots), centred, scaled up to FIT_MAX and, the
   first time, no smaller than READABLE; in fit mode it fits again when the map
   or the view changes size or a panel opens or closes (opened names the panels
   open), never while a panel is dragged. Panning or zooming by hand leaves fit
   mode, and Fit map returns to it. A click that does not drag calls onClear. */
function useCamera(w, h, docked, opened, onClear) {
  const viewport = useRef(null);
  const scroller = useRef(null);
  const [size, setSize] = useState({ w: 0, h: VIEW_MIN });
  const [cam, setCam] = useState(null);
  // The least scale fit mode keeps to, or null once the map is moved by hand.
  const fitting = useRef(READABLE);
  const measure = useCallback(() => {
    const el = viewport.current;
    if (!el) return;
    if (!scroller.current) scroller.current = scrollerOf(el);
    const next = { w: el.clientWidth, h: Math.max(VIEW_MIN, Math.round(toFoot(el, scroller.current) - FOOT)) };
    setSize((s) => (s.w === next.w && s.h === next.h ? s : next));
  }, []);
  // After every render, since what stands above the map moves it, and on any resize.
  useLayoutEffect(() => measure());
  useEffect(() => {
    const el = viewport.current;
    // What stands above the map takes its final height once the fonts are in.
    document.fonts?.ready.then(measure);
    window.addEventListener("resize", measure);
    const watch = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (el) watch?.observe(el); // a phone lists the boxes and draws no map
    return () => {
      window.removeEventListener("resize", measure);
      watch?.disconnect();
    };
  }, [measure]);
  const room = L.roomBeside(docked, size.w);
  const fit = useCallback((least) => {
    if (!size.w) return;
    const k = Math.max(Math.min(least, 1), Math.min(FIT_MAX, (room - 32) / w, (size.h - 32) / h));
    // Centred in the room where it fits, else from its top left.
    setCam({ k, x: w * k > room ? 16 : Math.round((room - w * k) / 2), y: h * k > size.h ? 4 : Math.round((size.h - h * k) / 2) });
  }, [size, w, h, room]);
  const latestFit = useRef(fit);
  latestFit.current = fit;
  useEffect(() => {
    if (fitting.current != null) latestFit.current(fitting.current);
  }, [size, w, h, opened]);
  const fitAll = () => {
    fitting.current = ZOOM_MIN;
    fit(ZOOM_MIN);
  };
  const now = cam || { k: 1, x: 0, y: 0 };
  const zoom = (by, cx = room / 2, cy = size.h / 2) => {
    fitting.current = null;
    setCam((c) => {
      const was = c || now;
      const k = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, was.k * by));
      return { k, x: cx - (cx - was.x) * (k / was.k), y: cy - (cy - was.y) * (k / was.k) };
    });
  };
  const latest = useRef(zoom);
  latest.current = zoom;
  useEffect(() => {
    const el = viewport.current;
    if (!el) return undefined;
    const wheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      latest.current(e.deltaY < 0 ? 1.1 : 1 / 1.1, e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);
  const held = useRef(null);
  const drag = {
    onPointerDown: (e) => {
      if (e.button !== 0 || e.target.closest("button, [role=button], .rd-box")) return;
      held.current = { x: e.clientX, y: e.clientY, from: now, moved: false };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e) => {
      const d = held.current;
      if (!d) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;
      if (!d.moved && Math.hypot(dx, dy) < 4) return;
      d.moved = true;
      fitting.current = null;
      setCam({ ...d.from, x: d.from.x + dx, y: d.from.y + dy });
    },
    onPointerUp: () => {
      const d = held.current;
      held.current = null;
      if (d && !d.moved) onClear();
    },
    onPointerCancel: () => {
      held.current = null;
    },
  };
  return { viewport, cam: now, size, zoom, fitAll, drag };
}

/* useGrab drags a panel by its head and brings the panel touched to the top.
   spots are where the panels stand now. */
function useGrab(onPanels, spots) {
  const held = useRef(null);
  useEffect(() => {
    const move = (e) => {
      const d = held.current;
      if (!d) return;
      const x = Math.max(0, d.from.x + e.clientX - d.x);
      const y = Math.max(0, d.from.y + e.clientY - d.y);
      onPanels((ps) => ps.map((p) => (p.id === d.id ? { ...p, x, y } : p)));
    };
    const drop = () => {
      held.current = null;
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", drop);
    window.addEventListener("pointercancel", drop);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", drop);
      window.removeEventListener("pointercancel", drop);
    };
  }, [onPanels]);
  return (id) => ({
    raise: () => onPanels((ps) => L.raisePanel(ps, id)),
    drag: (e) => {
      if (e.button !== 0 || e.target.closest("button")) return;
      held.current = { id, x: e.clientX, y: e.clientY, from: spots[id] };
      e.preventDefault();
    },
  });
}

/* One box on the map: its head, and once open its devices. */
function Box({ site, at, inside, picked, lit, dim, rowsLit, pickedRow, summary, onFold, onPick, onPickRow, onMore }) {
  const id = site.node_id;
  const state = W.readiness(site);
  return (
    <div className={`rd-box is-${state}${inside ? " is-open" : ""}${picked ? " is-on" : ""}${lit ? " is-lit" : ""}${dim ? " is-dim" : ""}`}
      style={{ left: at.x, top: at.y, width: at.w, height: at.h }} role="group" aria-label={id}>
      <div className="rd-box-head">
        <button type="button" className="rd-box-fold" aria-expanded={!!inside} aria-label={W.foldLabel(id, !!inside)} onClick={onFold}>
          {inside ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
        </button>
        <button type="button" className="rd-box-pick" aria-label={id} aria-pressed={picked} onClick={onPick}>
          <span className="rd-box-icon" aria-hidden="true"><TbRouter size={18} /></span>
          <span className="rd-box-title">
            <strong>{id}</strong>
            <span className="rd-mono">{W.lanOf(site) || W.READINESS.update}</span>
          </span>
          {state === "offline" && <em>{W.READINESS.offline}</em>}
        </button>
        <p className="rd-box-sum">{summary.map((line) => <span key={line} title={line}>{line}</span>)}</p>
      </div>
      {inside && (
        <>
          <ul className="rd-box-rows">
            {inside.rows.map((r) => (
              <li key={r.key}>
                <button type="button" aria-label={r.name} aria-pressed={pickedRow === r.key} onClick={() => onPickRow(r.key)}
                  className={`rd-box-row${r.entry ? " is-shared" : ""}${pickedRow === r.key ? " is-on" : ""}${rowsLit?.has(`${id}|${r.key}`) ? " is-lit" : ""}`}>
                  <span className="rd-box-row-icon" aria-hidden="true">{iconOf(r)}</span>
                  <span className="rd-box-row-name">
                    <span>{r.name}</span>
                    <span className="rd-mono">{r.net ? W.everyDevice(r.ip) : r.ip}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {inside.notes.map((n) => (n.more
            ? <button key="more" type="button" className="rd-box-note rd-link" onClick={onMore}>{n.text}</button>
            : <p key={n.text} className="rd-box-note">{n.text}</p>))}
        </>
      )}
    </div>
  );
}
